import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import whole30Background from '../../images/whole30.png';
import whole30FoodImage from '../../images/whole30-food.jpg';
import whole30DietBlogImage from '../../images/whole30-diet-blog.jpg';
import theWhole30DietImage from '../../images/the-whole30-diet.avif';

const PAGE_URL = 'https://createadish.com/dietary-guide/whole30';
const PAGE_TITLE = 'Whole30 Diet Guide: 30-Day Reset - Create A Dish';
const PAGE_DESCRIPTION = 'A 30-day nutritional reset that eliminates sugar, alcohol, grains, legumes, and dairy to identify food sensitivities and reset cravings.';
const PAGE_IMAGE = 'https://createadish.com/images/whole30-og.png';

const Whole30: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="whole30, nutritional reset, clean eating, elimination diet, food sensitivities, 30-day challenge" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Whole30 Diet Guide: 30-Day Reset",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "Reset, CleanEating, Nutritional"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${whole30Background})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Whole30</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>30-Day Reset</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Whole30
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The Whole30 Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#Reset', '#CleanEating', '#Nutritional'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Whole30 Diet, a 30-day nutritional reset that eliminates sugar, alcohol, grains, legumes, and dairy to identify food sensitivities and reset cravings. This program focuses on whole, unprocessed foods to promote better health habits and self-discovery.
          </Typography>
          <Box component="img" src={whole30FoodImage} alt="Whole30 foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Whole30 Diet was created in 2009 by Melissa and Dallas Hartwig, inspired by their personal health journeys and a desire to help others eliminate problematic foods. It gained popularity through their book "It Starts With Food" in 2012, emphasizing a strict 30-day elimination to reset the body.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Whole30 is a 30-day program with strict rules: no added sugars, alcohol, grains, legumes, dairy, or processed foods.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Proteins</strong>: Meat, seafood, eggs (e.g., chicken, salmon, beef).</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Vegetables</strong>: Unlimited; spinach, broccoli, carrots, potatoes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits</strong>: In moderation; apples, berries, bananas.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Nuts and Seeds</strong>: Almonds, walnuts (no peanuts).</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Fats</strong>: Avocados, olive oil, coconut oil.</Typography>
            </Box>
            <Box component="img" src={whole30DietBlogImage} alt="Whole30 diet blog" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Whole30 aims to reduce inflammation, improve digestion, boost energy, enhance sleep, and reset cravings. It may aid weight loss, alleviate food sensitivities, and promote non-scale victories like better mood and skin.
            </Typography>
            <Box component="img" src={theWhole30DietImage} alt="The Whole30 diet" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Whole30 Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Prepare Thoroughly</strong>: Read the rules, stock compliant foods, and plan meals.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Focus on Mindset</strong>: Embrace non-scale victories and community support.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Reintroduction Phase</strong>: Slowly add back foods post-30 days to identify issues.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI</strong>: Generate recipes on CreateADish.com, like "Whole30 egg scramble."</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Whole30;
